import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createSlaPolicy,
  type DeleteSlaPoliciesInput,
  deleteSlaPolicies,
  getAllSlaPolicies,
  getCustomerEffectiveDefault,
  getSlaPolicyById,
  getTicketSlaStatus,
  setSlaPolicyAsDefault,
  toggleSlaPolicy,
  updateSlaPolicy,
} from "controller/api";
import type {
  ApiErrorType,
  CreateSlaPolicyInput,
  SlaPolicyResponse,
  UpdateSlaPolicyInput,
} from "shared";
import { sentryLogger } from "utilz";

interface UseGetAllSlaPoliciesProps {
  search?: string;
  page?: number;
  perPage?: number;
  sortColumn?: string;
  sortDirection?: string;
  enabled?: boolean;
  filter?: Record<string, unknown>;
}

export const useGetAllSlaPolicies = ({
  search,
  page = 1,
  perPage = 50,
  sortColumn = "created_at",
  sortDirection = "DESC",
  enabled = true,
  filter,
}: UseGetAllSlaPoliciesProps) => {
  return useQuery({
    queryKey: ["sla", search, page, perPage, sortColumn, sortDirection, filter],
    enabled,
    queryFn: () =>
      getAllSlaPolicies({
        page,
        perPage,
        sortColumn,
        sortDirection,
        search,
        filter,
      }),
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "getAllSlaPolicies",
      }),
  });
};

export const useDeleteSlaPolicies = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (input: DeleteSlaPoliciesInput) => deleteSlaPolicies(input),
    {
      onSuccess() {
        queryClient.invalidateQueries(["sla"]);
      },
      onError(error: ApiErrorType) {
        sentryLogger({
          error,
          workflow: "controlCenter",
          level: "error",
          functionName: "deleteSlaPolicies",
        });
      },
    },
  );
};

export const useGetTicketSlaStatus = ({
  ticketId,
  enabled = true,
}: {
  ticketId: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: ["sla-status", ticketId],
    queryFn: () => getTicketSlaStatus(ticketId),
    enabled: enabled && !!ticketId,
    refetchInterval: 30000,
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error,
        workflow: "ticket",
        level: "error",
        functionName: "getTicketSlaStatus",
      }),
  });
};

export const useCreateSlaPolicy = () => {
  const queryClient = useQueryClient();
  return useMutation((data: CreateSlaPolicyInput) => createSlaPolicy(data), {
    onSuccess() {
      queryClient.invalidateQueries(["sla"]);
    },
    onError(error: ApiErrorType) {
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "createSlaPolicy",
      });
    },
  });
};

export const useUpdateSlaPolicy = () => {
  const queryClient = useQueryClient();
  return useMutation((data: UpdateSlaPolicyInput) => updateSlaPolicy(data), {
    onSuccess() {
      queryClient.invalidateQueries(["sla"]);
    },
    onError(error: ApiErrorType) {
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "updateSlaPolicy",
      });
    },
  });
};

export const useFetchSlaPolicyById = () => {
  return useMutation((policyId: string) => getSlaPolicyById(policyId), {
    onError(error: ApiErrorType) {
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "getSlaPolicyById",
      });
    },
  });
};

export const useGetSlaPolicyById = ({
  slaId,
  enabled = true,
}: {
  slaId: string | undefined;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: ["sla", slaId],
    queryFn: () => getSlaPolicyById(slaId as string),
    enabled: enabled && !!slaId,
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "getSlaPolicyById",
      }),
  });
};

export const useToggleSlaPolicy = () => {
  const queryClient = useQueryClient();
  return useMutation((policyId: string) => toggleSlaPolicy(policyId), {
    onSuccess() {
      queryClient.invalidateQueries(["sla"]);
    },
    onError(error: ApiErrorType) {
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "toggleSlaPolicy",
      });
    },
  });
};

export const useSetSlaPolicyAsDefault = () => {
  const queryClient = useQueryClient();
  return useMutation((policyId: string) => setSlaPolicyAsDefault(policyId), {
    onSuccess() {
      // Invalidate the SLA list so the old + new default rows both refresh.
      queryClient.invalidateQueries(["sla"]);
    },
    onError(error: ApiErrorType) {
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "setSlaPolicyAsDefault",
      });
    },
  });
};

/**
 * Backward-compatible wrapper for consumers that still use the old
 * `useGetAllSla` hook (ticket forms, filters, automation nodes, etc.).
 * Transforms the new SLA service response to the legacy shape:
 *   `{ result: [{ mapping_id, sla_name, ... }], totalPages }`
 *
 * @deprecated Use `useGetAllSlaPolicies` for new code.
 */
interface UseGetAllSlaLegacyProps {
  sortColumn?: string;
  sortDirection?: string;
  searchQuery?: string;
  page?: number;
  perPageCount?: number;
  filter?: Record<string, unknown>;
  enabled?: boolean;
}

export const useGetAllSla = ({
  sortColumn = "created_at",
  sortDirection = "DESC",
  searchQuery,
  page = 1,
  perPageCount = 50,
  filter,
  enabled = true,
}: UseGetAllSlaLegacyProps) => {
  const query = useGetAllSlaPolicies({
    search: searchQuery,
    page,
    perPage: perPageCount,
    sortColumn,
    sortDirection,
    filter,
    enabled,
  });

  const legacyData:
    | {
        result: Record<string, unknown>[];
        total: number;
        totalPages: number;
      }
    | undefined = query.data
    ? {
        result: query.data.data.map((policy: SlaPolicyResponse) => ({
          ...policy,
          mapping_id: policy.id,
          sla_name: policy.name,
          meta: { is_default: policy.isDefault },
        })),
        total: query.data.total,
        totalPages: query.data.totalPages,
      }
    : undefined;

  return { ...query, data: legacyData };
};

/**
 * @deprecated Use `useCreateSlaPolicy` for new code.
 */
export const useCreateSla = useCreateSlaPolicy;

/**
 * @deprecated Use `useUpdateSlaPolicy` for new code.
 */
export const useUpdateSla = useUpdateSlaPolicy;

/**
 * @deprecated Use `useFetchSlaPolicyById` for new code.
 */
export const useFetchSlaByIdUsingMutation = useFetchSlaPolicyById;

/**
 * Backward-compatible wrapper that fetches SLA rules by policy ID.
 * Transforms `priorityTargets` to the legacy `sla_rules` shape.
 *
 * @deprecated Use `useFetchSlaPolicyById` and access `priorityTargets` directly.
 */
export const useGetSlaRules = ({
  slaId,
  enabled = true,
}: {
  slaId: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: ["sla-rules", slaId],
    queryFn: async () => {
      const policy = await getSlaPolicyById(slaId);
      return {
        sla_rules: (policy.priorityTargets || []).map((target) => ({
          priority: target.priority,
          first_response: `${target.firstResponseMinutes ?? 0} minutes`,
          resolution_time: `${target.resolutionMinutes ?? 0} minutes`,
          warning_time: `${Math.round(
            ((target.resolutionMinutes ?? 0) *
              (policy.atRiskThresholdPercent ?? 80)) /
              100,
          )} minutes`,
        })),
      };
    },
    enabled: enabled && !!slaId,
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error,
        workflow: "ticket",
        level: "error",
        functionName: "getSlaPolicyById",
      }),
  });
};

/**
 * Fetch the effective default SLA policy for a specific customer.
 * The result reflects the customer-level override when set, or the MSP-wide
 * default as a fallback. Use this (not `policy.isDefault`) when determining
 * which badge to display in a customer-scoped SLA list.
 */
export const useCustomerEffectiveDefault = (
  customerId: string,
  enabled = true,
) =>
  useQuery({
    queryKey: ["sla", "customer-effective-default", customerId],
    queryFn: () => getCustomerEffectiveDefault(customerId),
    enabled: enabled && !!customerId,
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "getCustomerEffectiveDefault",
      }),
  });
